import { inputPlaceholderVariants, inputVariants } from '@ui/components';
import { VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '@ui/utils';

import type { SecureFormChangeResponse, SecureForm } from '../types/payu';

import { usePayUFormContext } from '../hooks/use-payu-form';

interface InputProps
  extends React.ComponentProps<'div'>,
    Omit<VariantProps<typeof inputVariants>, 'hasError' | 'focused' | 'empty'> {
  trailingIcon?: {
    position: 'right' | 'left';
    icon: React.ReactNode;
    onClick?: () => void;
  };
  validationErrors?: { message: string; code: string }[];
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  floating?: boolean;
  name: string;
  payUControlType: 'number' | 'date' | 'cvv';
  id: string;
}

interface InputPlaceholderProps extends VariantProps<typeof inputPlaceholderVariants> {
  placeholder?: string;
  floating?: boolean;
  name: string;
}

const inputExtendedVariants = cva(`items-center w-full`, {
  variants: {
    focused: {
      true: 'focused outline-surface outline-1 ring-3 ring-border-sky ring-offset-0',
    },
    empty: {
      true: 'empty',
    },
    disabled: {
      true: 'disabled text-muted-foreground outline-border-disabled cursor-not-allowed bg-body outline-none text-content-disabled',
    },
  },
  defaultVariants: {
    focused: false,
    empty: false,
    disabled: false,
  },
});

const PayUInput = React.forwardRef<HTMLDivElement, InputProps>(
  (
    {
      validationErrors,
      placeholder = '',
      floating = false,
      payUControlType,
      className,
      inputSize,
      hasShadow,
      disabled,
      id,
      ...props
    },
    _,
  ) => {
    const { controls } = usePayUFormContext();

    const payUFormRef = React.useRef<SecureForm>(null);
    const controlRef = React.useRef<HTMLDivElement>(null);

    const [isFocused, setIsFocused] = React.useState(false);
    const [isEmpty, setIsEmpty] = React.useState(true);

    const [error, setError] = React.useState<{ message: string; code: string }[] | false>(false);
    const errorRef = React.useRef<{ message: string; code: string }>(null);
    const [displayedError, setDisplayedError] = React.useState<{
      message: string;
      code: string;
    } | null>(null);

    const handleChange = React.useCallback(
      (event: SecureFormChangeResponse) => {
        setIsEmpty(event.empty);
        setError(event.error);
        errorRef.current = (event.error && event.error[0]) || null;
      },
      [setIsEmpty, setError],
    );

    const handleFocus = React.useCallback(() => {
      setIsFocused(true);
    }, [setIsFocused]);

    const handleBlur = React.useCallback(() => {
      setIsFocused(false);
      setDisplayedError(errorRef.current);
    }, [setIsFocused, setDisplayedError]);

    React.useEffect(() => {
      if (
        !controls ||
        !id ||
        !payUControlType ||
        !controlRef.current ||
        controlRef.current.children.length
      )
        return;

      payUFormRef.current = controls[payUControlType].render(`#${id}`);
      payUFormRef.current.on('change', handleChange);
      payUFormRef.current.on('focus', handleFocus);
      payUFormRef.current.on('blur', handleBlur);
    }, [controls, id, payUControlType, handleChange, handleFocus, handleBlur]);

    React.useEffect(() => {
      setError(validationErrors?.length ? validationErrors : false);
      errorRef.current = (validationErrors && validationErrors[0]) || null;
      setDisplayedError(errorRef.current);
    }, [validationErrors]);

    return (
      <div className={cn('relative z-0', className)}>
        <div
          className={cn(`relative w-full cursor-text`, {
            'cursor-not-allowed pointer-events-none': disabled,
          })}
        >
          <div
            className={cn(
              inputVariants({
                inputSize,
                hasError: !!error && !isFocused,
                floating,
                hasShadow,
              }),
              inputExtendedVariants({
                empty: isEmpty,
                focused: isFocused,
                disabled,
              }),
            )}
            onClick={() => {
              payUFormRef.current?.focus();
            }}
            ref={controlRef}
            id={id}
            {...props}
          />

          {placeholder && (
            <PayUInputPlaceholder
              placeholder={placeholder}
              required={props.required}
              floating={floating}
              size={inputSize}
              name={id || ''}
            />
          )}
        </div>
        {displayedError && (
          <p className="text-[.625rem] mt-1 font-medium text-critical">{displayedError.message}</p>
        )}
      </div>
    );
  },
);
PayUInput.displayName = 'PayUInput';

const PayUInputPlaceholder = ({
  placeholder,
  iconOffset,
  required,
  floating,
  name,
  size,
}: InputPlaceholderProps) => {
  return (
    <label
      className={cn(
        inputPlaceholderVariants({ iconOffset, size, required }),
        floating
          ? `peer-[&.empty]:scale-100 peer-[&.empty]:translate-y-0 
         peer-[&.focused]:scale-75 peer-[&.focused]:-translate-y-2.5 
         peer-[&.disabled]:z-0 peer-[&.disabled]:text-content-disabled`
          : `scale-100 translate-y-0 pointer-events-none hidden 
          peer-[&.empty]:inline peer-[&.disabled]:z-0 
          peer-[&.disabled]:text-content-disabled`,
      )}
      htmlFor={name}
    >
      {placeholder}
    </label>
  );
};

export { PayUInput };
